import {
  parseISO,
  subMonths,
  subYears,
  subWeeks,
  subDays,
  addMonths,
  addWeeks,
  addDays,
  format,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  startOfYear,
  endOfYear,
  isBefore,
} from 'date-fns'
import type { Timeframe } from '@/api/generated/models'

export type DataFrequency = 'daily' | 'weekly' | 'monthly'
export type DateAnchor = 'start_date' | 'end_date'

export type ModelingPreset =
  | 'last_12_months'
  | 'last_2_years'
  | 'last_3_years'
  | 'last_5_years'
  | 'last_full_year'
  | 'last_2_full_years'
  | 'last_3_full_years'
  | 'last_5_full_years'

export const MODELING_PRESETS: { label: string; value: ModelingPreset }[] = [
  { label: 'Last 12 months', value: 'last_12_months' },
  { label: 'Last 2 years', value: 'last_2_years' },
  { label: 'Last 3 years', value: 'last_3_years' },
  { label: 'Last 5 years', value: 'last_5_years' },
  { label: 'Last full year', value: 'last_full_year' },
  { label: 'Last 2 full years', value: 'last_2_full_years' },
  { label: 'Last 3 full years', value: 'last_3_full_years' },
  { label: 'Last 5 full years', value: 'last_5_full_years' },
]

/** Format a Date to YYYY-MM-DD string */
export const formatDate = (date: Date): string => format(date, 'yyyy-MM-dd')

/**
 * Safely parse a date string to a Date object.
 * Appends T00:00:00 to avoid timezone-related off-by-one errors when
 * the string is just a date without time.
 */
const safeParse = (dateStr: string): Date => parseISO(dateStr)

export const calculateModelingTimeframe = (
  preset: ModelingPreset,
  endAt: string,
): Timeframe => {
  const endDate = safeParse(endAt)

  switch (preset) {
    case 'last_12_months':
      return {
        from: formatDate(subMonths(endDate, 12)),
        to: formatDate(endDate),
      }

    case 'last_2_years':
      return {
        from: formatDate(subYears(endDate, 2)),
        to: formatDate(endDate),
      }

    case 'last_3_years':
      return {
        from: formatDate(subYears(endDate, 3)),
        to: formatDate(endDate),
      }

    case 'last_5_years':
      return {
        from: formatDate(subYears(endDate, 5)),
        to: formatDate(endDate),
      }

    case 'last_full_year': {
      // Most recent completed calendar year.
      // If end_at is in 2026, the last full year is 2025.
      const lastFullYear = endDate.getFullYear() - 1
      return {
        from: formatDate(startOfYear(new Date(lastFullYear, 0, 1))),
        to: formatDate(endOfYear(new Date(lastFullYear, 0, 1))),
      }
    }

    case 'last_2_full_years': {
      const lastFullYear = endDate.getFullYear() - 1
      return {
        from: formatDate(startOfYear(new Date(lastFullYear - 1, 0, 1))),
        to: formatDate(endOfYear(new Date(lastFullYear, 0, 1))),
      }
    }

    case 'last_3_full_years': {
      const lastFullYear = endDate.getFullYear() - 1
      return {
        from: formatDate(startOfYear(new Date(lastFullYear - 2, 0, 1))),
        to: formatDate(endOfYear(new Date(lastFullYear, 0, 1))),
      }
    }

    case 'last_5_full_years': {
      const lastFullYear = endDate.getFullYear() - 1
      return {
        from: formatDate(startOfYear(new Date(lastFullYear - 4, 0, 1))),
        to: formatDate(endOfYear(new Date(lastFullYear, 0, 1))),
      }
    }
  }
}

/**
 * Determine which presets should be disabled because their calculated
 * `from` date would be before the dependent driver's `start_at`.
 */
export const getDisabledPresets = (
  startAt: string,
  endAt: string,
): ModelingPreset[] => {
  const driverStart = safeParse(startAt)
  const disabled: ModelingPreset[] = []

  for (const { value: preset } of MODELING_PRESETS) {
    const { from } = calculateModelingTimeframe(preset, endAt)
    const presetStart = safeParse(from)

    if (isBefore(presetStart, driverStart)) {
      disabled.push(preset)
    }
  }

  return disabled
}

/** Get the display label for a frequency's unit */
export const getUnitLabel = (frequency: DataFrequency): string => {
  switch (frequency) {
    case 'daily':
      return 'days'
    case 'weekly':
      return 'weeks'
    case 'monthly':
      return 'months'
  }
}

/**
 * Get the minimum optimization period count for a frequency.
 * All frequencies require roughly 4 weeks minimum:
 *   daily → 28 days, weekly → 4 weeks, monthly → 1 month
 */
export const getMinOptimizePeriod = (frequency: DataFrequency): number => {
  switch (frequency) {
    case 'daily':
      return 28
    case 'weekly':
      return 4
    case 'monthly':
      return 1
  }
}

/**
 * Get the maximum optimization period count that fits within the
 * training timeframe, expressed in the given frequency unit.
 */
export const getMaxOptimizePeriod = (
  frequency: DataFrequency,
  trainingFrom: string,
  trainingTo: string,
): number => {
  if (!trainingFrom || !trainingTo) return 0

  const start = safeParse(trainingFrom)
  const end = safeParse(trainingTo)

  switch (frequency) {
    case 'daily':
      return differenceInDays(end, start)
    case 'weekly':
      return differenceInWeeks(end, start)
    case 'monthly':
      return differenceInMonths(end, start)
  }
}

/**
 * Default optimization period: 20% of the modeling window, rounded to a
 * whole number and clamped to [min, max] for the selected frequency.
 */
export const getDefaultOptimizePeriod = (
  frequency: DataFrequency,
  maxPeriod: number,
): number => {
  const minPeriod = getMinOptimizePeriod(frequency)
  if (maxPeriod <= 0) return minPeriod
  const twentyPercent = Math.round(maxPeriod * 0.2)
  return Math.min(maxPeriod, Math.max(minPeriod, twentyPercent))
}

/**
 * Calculate the optimize timeframe from a period count, an anchor date,
 * a frequency unit, and a direction (from_start or from_end).
 *
 * - from_end:   to = anchorDate, from = anchorDate − count units
 * - from_start: from = anchorDate, to = anchorDate + count units
 */
export const calculateOptimizeTimeframe = (
  count: number,
  anchorDate: string,
  frequency: DataFrequency,
  direction: DateAnchor,
): Timeframe => {
  const anchor = safeParse(anchorDate)

  if (direction === 'end_date') {
    // Anchor is the end date — subtract to find start
    let startDate: Date
    switch (frequency) {
      case 'daily':
        startDate = subDays(anchor, count)
        break
      case 'weekly':
        startDate = subWeeks(anchor, count)
        break
      case 'monthly':
        startDate = subMonths(anchor, count)
        break
    }
    return { from: formatDate(startDate), to: formatDate(anchor) }
  } else {
    // Anchor is the start date — add to find end
    let endDate: Date
    switch (frequency) {
      case 'daily':
        endDate = addDays(anchor, count)
        break
      case 'weekly':
        endDate = addWeeks(anchor, count)
        break
      case 'monthly':
        endDate = addMonths(anchor, count)
        break
    }
    return { from: formatDate(anchor), to: formatDate(endDate) }
  }
}

/**
 * Infer the optimization period count from a stored optimize timeframe.
 * Inverse of calculateOptimizeTimeframe.
 */
export const inferOptimizePeriodCount = (
  optimizeTimeframe: Timeframe | undefined,
  frequency: DataFrequency,
): number | '' => {
  if (!optimizeTimeframe?.from || !optimizeTimeframe?.to) return ''

  const start = safeParse(optimizeTimeframe.from)
  const end = safeParse(optimizeTimeframe.to)
  if (isBefore(end, start)) return ''

  switch (frequency) {
    case 'daily':
      return differenceInDays(end, start)
    case 'weekly':
      return differenceInWeeks(end, start)
    case 'monthly':
      return differenceInMonths(end, start)
  }
}

export const inferOptimizeDateAnchor = (
  optimizeTimeframe: Timeframe | undefined,
  trainingTimeframe: Timeframe | undefined,
): DateAnchor => {
  if (
    optimizeTimeframe?.from &&
    trainingTimeframe?.from &&
    optimizeTimeframe.from === trainingTimeframe.from
  ) {
    return 'start_date'
  }
  return 'end_date'
}
